/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import { Icon } from 'sarsaparilla';

import { trackClick } from './DatePicker.tracking';
import type { DatePickerShell } from './DatePicker.types';
import type { FlexDatePickerSize } from './FlexDatePicker.types';

import styles from './DatePickerTrigger.module.scss';

interface TriggerPartProps {
    className?: string;
    disabled?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    children?: React.ReactNode;
}

interface DatePickerTriggerProps {
    /** The wrapper's base-ui trigger part, e.g. `Popover.Trigger` or `Drawer.Trigger`. */
    TriggerPart: React.ComponentType<TriggerPartProps>;
    shell: DatePickerShell;
    isDisabled?: boolean;
    clickTagCategory?: string;
    size?: FlexDatePickerSize;
    /** Screen-reader prefix that names the field. */
    label?: string;
    /** Accessible name of the inline clear button. */
    clearLabel?: string;
}

/**
 * Field-style button that opens the date picker, plus an inline clear button.
 * Shared by every wrapper; the wrapper supplies its own trigger part.
 */
export function DatePickerTrigger({
    TriggerPart,
    shell,
    isDisabled,
    clickTagCategory,
    size = 'md',
    label = 'Check In and Check Out Dates:',
    clearLabel = 'Clear dates',
}: DatePickerTriggerProps) {
    const { isOpen, triggerContent, currentTabHasDates, hasDates, onClearDates } = shell;

    return (
        <div className={cx(styles.triggerContainer, { [styles.lg]: size === 'lg' })}>
            <TriggerPart
                className={styles.triggerButton}
                disabled={isDisabled}
                onClick={() =>
                    trackClick(
                        clickTagCategory,
                        isOpen ? 'Hide Dates Overlay' : 'Show Dates Overlay'
                    )
                }
            >
                <Icon iconName="calendar" className={styles.calendarIcon} />
                {/* Names the field, since the visible text is only the selection. */}
                <span className="rec-sr-only">{label}</span>{' '}
                <span
                    data-truncate-text
                    className={currentTabHasDates ? 'color-text-01' : 'color-text-03'}
                >
                    {triggerContent}
                </span>
            </TriggerPart>
            <button
                type="button"
                disabled={!hasDates}
                aria-hidden={!hasDates}
                aria-label={clearLabel}
                className={cx(styles.topLevelClearButton, {
                    [styles.visible]: hasDates,
                })}
                onClick={() => onClearDates({ commit: true })}
            >
                <Icon iconName="close" className={styles.closeIcon} />
            </button>
        </div>
    );
}
